import type {
  CorrectionDraft,
  FieldDescriptor,
  ImportFilters,
  ImportProvider,
  ImportedJobDetail,
  ImportedJobSummary,
  JsonObject,
  JsonValue,
  RuleKind,
  RuleNode,
  RuleOperator,
  ScalarType,
} from './types';
import { ruleOperators } from './types';

export function queryText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// The Needs attention compartments, most common first; the server counts and filters by the same keys.
export const attentionReasons = [
  'review-changes',
  'correct-information',
  'resolve-blocker',
  'approve',
  'retry-processing',
  'fix-configuration',
  'assign-salesperson',
];

export function filtersFromQuery(query: Record<string, unknown>): ImportFilters {
  const page = Number(queryText(query.page));
  const view = queryText(query.view) === 'all' || (!queryText(query.view) && queryText(query.status))
    ? 'all'
    : 'attention';
  const reason = queryText(query.reason);

  return {
    view,
    search: queryText(query.search),
    format: queryText(query.format),
    company: queryText(query.company),
    status: queryText(query.status),
    reason: view === 'attention' && attentionReasons.includes(reason) ? reason : '',
    page: Number.isSafeInteger(page) && page > 0 ? page : 1,
  };
}


export function fieldValue(document: JsonObject, path: string): JsonValue | undefined {
  let value: JsonValue | undefined = document;

  for (const part of path.split('.')) {
    if (Array.isArray(value)) {
      value = /^(0|[1-9][0-9]*)$/.test(part) ? value[Number(part)] : undefined;
      continue;
    }

    if (!value || typeof value !== 'object') {
      return undefined;
    }

    value = Object.prototype.hasOwnProperty.call(value, part) ? value[part] : undefined;
  }

  return value;
}

export function displayValue(value: JsonValue | undefined): string {
  if (value === undefined) {
    return '\u2014';
  }

  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

export function parseTypedValue(text: string, type: ScalarType): JsonValue {
  if (type === 'Number') {
    if (!text.trim() || !Number.isFinite(Number(text))) {
      throw new Error('Number');
    }

    return Number(text);
  }

  if (type === 'Boolean') {
    if (text !== 'true' && text !== 'false') {
      throw new Error('Boolean');
    }

    return text === 'true';
  }

  if (
    type === 'Date' &&
    (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}(T.*)?$/.test(text) || !Number.isFinite(Date.parse(text)))
  ) {
    throw new Error('Date');
  }

  return text;
}

export function correctionPayload(
  drafts: Record<string, CorrectionDraft>,
  fields: FieldDescriptor[]
): { changes: JsonObject; resetPaths: string[] } {
  const changes: JsonObject = {};
  const resetPaths: string[] = [];

  for (const path of Object.keys(drafts)) {
    const draft = drafts[path];
    const field = fields.find(
      (item) => item.source === 'effective' && item.editable && item.path === path
    );

    if (!field || field.valueType === 'object' || field.valueType === 'array') {
      throw new Error(path);
    }

    if (draft.mode === 'reset') {
      resetPaths.push(path);
    } else {
      try {
        changes[path] =
          draft.mode === 'null'
            ? null
            : parseTypedValue(
                draft.text,
                (field.valueType.charAt(0).toUpperCase() + field.valueType.slice(1)) as ScalarType
              );
      } catch {
        throw new Error(path);
      }
    }
  }

  return { changes, resetPaths };
}

export function createRule(kind: RuleKind = 'Condition'): RuleNode {
  if (kind === 'All' || kind === 'Any') {
    return { kind, children: [] };
  }

  if (kind === 'AnyElement') {
    return { kind, source: 'Raw', path: '', children: [createRule('All')] };
  }

  return { kind, source: 'Raw', path: '', valueType: 'String', operator: 'equals', value: '' };
}

export function operatorsForType(type: ScalarType): RuleOperator[] {
  return ruleOperators.filter((operator) => {
    if (operator === 'contains' || operator === 'startsWith') {
      return type === 'String';
    }

    if (['gt', 'gte', 'lt', 'lte'].includes(operator)) {
      return type === 'Number' || type === 'Date';
    }

    return true;
  });
}

// Basic editor completeness; the API remains authoritative for field and rule validation.
export function isRuleComplete(node: RuleNode): boolean {
  const children = node.children || [];

  if (node.kind === 'All' || node.kind === 'Any') {
    return children.length > 0 && children.every(isRuleComplete);
  }

  if (!node.path?.trim()) {
    return false;
  }

  if (node.kind === 'AnyElement') {
    return children.length === 1 && isRuleComplete(children[0]);
  }

  const type = node.valueType || 'String';
  if (!node.operator || !operatorsForType(type).includes(node.operator)) {
    return false;
  }

  if (node.operator === 'isMissing' || node.operator === 'isPresent') {
    return true;
  }

  const values = node.operator === 'in' ? node.value : [node.value];
  if (!Array.isArray(values) || !values.length) {
    return false;
  }

  return values.every((value) => {
    if (type === 'Number') {
      return typeof value === 'number' && Number.isFinite(value);
    }

    if (type === 'Boolean') {
      return typeof value === 'boolean';
    }

    if (typeof value !== 'string') {
      return false;
    }

    try {
      parseTypedValue(value, type);
      return true;
    } catch {
      return false;
    }
  });
}

// Paths inside an element scope must refer to that same element, not the document root.
export function scopedFields(
  fields: FieldDescriptor[],
  source: string,
  scope: string
): FieldDescriptor[] {
  const prefix = scope ? `${scope.replace(/\[\]/g, '').replace(/\.[0-9]+(?=\.|$)/g, '')}.` : '';

  const scoped = fields
    .filter(
      (field) =>
        field.source === source.toLowerCase() &&
        (!prefix ||
          field.path
            .replace(/\[\]/g, '')
            .replace(/\.[0-9]+(?=\.|$)/g, '')
            .startsWith(prefix))
    )
    .map((field) => ({
      ...field,
      path: prefix
        ? field.path
            .replace(/\[\]/g, '')
            .replace(/\.[0-9]+(?=\.|$)/g, '')
            .slice(prefix.length)
        : field.path,
    }));
  const result: FieldDescriptor[] = [];

  for (const field of scoped) {
    const existing = result.find(
      (item) => item.path === field.path && item.valueType === field.valueType
    );
    if (!existing) {
      result.push({ ...field, samples: field.samples.slice() });
      continue;
    }

    for (const sample of field.samples) {
      if (!existing.samples.some((value) => JSON.stringify(value) === JSON.stringify(sample))) {
        existing.samples.push(sample);
      }
    }
  }

  return result;
}

export function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } }).response?.status;
}

export function validationMessages(error: unknown): string[] {
  const body = (
    error as {
      response?: { data?: { errors?: Record<string, string[]>; message?: string; title?: string } };
    }
  ).response?.data;

  if (!body) {
    return [];
  }

  const errors = body.errors;

  if (errors && typeof errors === 'object') {
    return Object.keys(errors).reduce<string[]>((result, key) => result.concat(errors[key]), []);
  }

  return body.message ? [body.message] : body.title ? [body.title] : [];
}

export function recordCanMutate(
  record: Pick<ImportedJobDetail, 'providerMode' | 'canMutate'> | null,
  available: boolean
): boolean {
  return !!record && available && record.providerMode === 'StagedImport' && record.canMutate === true;
}

export function comparisonFields(fields: FieldDescriptor[]): FieldDescriptor[] {
  return fields.filter((field) => field.source === 'effective');
}

export function providerAvailable(providers: ImportProvider[], selected: string): boolean {
  return providers.some((provider) => provider.key === selected);
}

export function providerOptions(providers: ImportProvider[], selected: string) {
  const options = providers.map((provider) => ({
    key: provider.key,
    name: provider.name,
    disabled: false,
  }));

  if (selected && !providerAvailable(providers, selected)) {
    options.unshift({ key: selected, name: selected, disabled: true });
  }

  return options;
}

function xmlElementValue(element: Element): JsonValue {
  const result: JsonObject = {};
  Array.from(element.attributes).forEach((attribute) => {
    result[`@${attribute.name}`] = attribute.value;
  });

  const children = Array.from(element.children);
  if (!children.length) {
    const text = (element.textContent || '').trim();
    if (!Object.keys(result).length) return text;
    if (text) result.value = text;
    return result;
  }

  children.forEach((child) => {
    const value = xmlElementValue(child);
    const existing = result[child.nodeName];
    if (existing === undefined) result[child.nodeName] = value;
    else if (Array.isArray(existing)) existing.push(value);
    else result[child.nodeName] = [existing, value];
  });
  return result;
}

/** Parses a provider payload (JSON or XML) for display; returns null when it is neither. */
export function parseProviderPayload(raw: string): JsonValue | null {
  const text = (raw || '').trim();
  if (!text) return null;
  if (text.startsWith('<')) {
    const document = new DOMParser().parseFromString(text, 'application/xml');
    if (document.getElementsByTagName('parsererror').length) return null;
    const root = document.documentElement;
    return { [root.nodeName]: xmlElementValue(root) };
  }
  try {
    return JSON.parse(text) as JsonValue;
  } catch {
    return null;
  }
}

export function recruitJobUrl(companyId: number, jobAdId: number): string {
  return `https://recruit.ledigajobb.se/company/${companyId}/jobs/${jobAdId}`;
}

// Why a record waits for approval; the connection's manual approval is only one of the reasons.
export function approvalReasons(detail: ImportedJobDetail, manualApproval: boolean | null): string[] {
  if (detail.approvalState !== 'pending' && !detail.reviewRequired) return [];

  const reasons: string[] = [];
  if (detail.issues.some((issue) => issue.code === 'MatrixTags')) reasons.push('matrix-tags');
  if (detail.reviewRequired) reasons.push('live-update');
  if (manualApproval && detail.approvalState === 'pending') reasons.push('manual');

  return reasons;
}

// Refreshes one list row from its updated record. The row stays in place even if it no longer matches the filter.
export function withUpdatedRow(items: ImportedJobSummary[], updated: ImportedJobSummary): ImportedJobSummary[] {
  return items.map((item) =>
    item.id === updated.id
      ? (Object.keys(item) as (keyof ImportedJobSummary)[]).reduce(
          (row, key) => ({ ...row, [key]: updated[key] }),
          item
        )
      : item
  );
}
